import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { ArrowLeft, Heart, Eye, CheckCircle2, Ribbon, Send, ShoppingBag, Gavel, Gift, Trophy, CreditCard, X, MapPin, Truck, HandHeart, Flag, Ban, Trash2, VolumeX, Radio, Square, Calendar, Sparkles, Play, Clock, Maximize2, Minimize2, Star } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusPill } from "@/components/StatusPill";
import { Confetti } from "@/components/Confetti";
import { Loading, Empty, money } from "@/components/common";
import LiveVideoPlayer from "@/components/LiveVideoPlayer";
import ReportModal from "@/components/ReportModal";
import AddressPicker from "@/components/AddressPicker";
import { realtime } from "@/lib/realtime";

function useCountdown(endIso) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (!endIso) { setLeft(0); return; }
    const tick = () => setLeft(Math.max(0, Math.floor((new Date(endIso) - new Date()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endIso]);
  return left;
}

export default function ShowDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const stageRef = useRef(null);
  const [isFs, setIsFs] = useState(false);
  useEffect(() => {
    const onFs = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);
  const [hearts, setHearts] = useState(0);
  const [busy, setBusy] = useState(null);
  const [chat, setChat] = useState([]);
  const [chatMe, setChatMe] = useState(null);
  const [chatSlow, setChatSlow] = useState(0);
  const [chatMuted, setChatMuted] = useState([]);
  const [msg, setMsg] = useState("");
  const [liveViewers, setLiveViewers] = useState(null);
  const [roster, setRoster] = useState([]);
  const [reportTarget, setReportTarget] = useState(null);
  const [floatRx, setFloatRx] = useState([]);
  const [rxTotal, setRxTotal] = useState(0);
  const [rxEmojis, setRxEmojis] = useState(["❤️", "🔥", "👏", "😍", "💰", "🤲"]);
  const seenRx = useRef(new Set());
  const rxKey = useRef(0);
  const [confetti, setConfetti] = useState(false);
  const [checkoutLot, setCheckoutLot] = useState(null);
  const [donate, setDonate] = useState(null);
  const [shout, setShout] = useState(null);
  const shoutTimer = useRef(null);
  const [simulcast, setSimulcast] = useState(null);
  const [simBusy, setSimBusy] = useState(false);
  const cart = useCart();
  const chatEnd = useRef(null);
  const chatBox = useRef(null);
  const prevChatLen = useRef(0);
  const settledRef = useRef({});
  const prevWinner = useRef(null);

  const load = async (silent) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.get(`/shows/${id}`);
      setData(res);
      setHearts(res.show.hearts || 0);
    } catch { if (!silent) setData(null); }
    finally { if (!silent) setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Poll live shows for reconciliation (bids/giveaway); WebSocket handles instant bid updates.
  useEffect(() => {
    if (data?.show?.status !== "live") return;
    const iv = setInterval(() => load(true), 10000);
    return () => clearInterval(iv);
  }, [data?.show?.status, id]);

  // Live chat: initial load + slow reconciliation poll (WebSocket delivers instant messages).
  const loadChat = async () => {
    try { const r = await api.get(`/shows/${id}/chat`); setChat(r.messages); setChatMe(r.me); setChatSlow(r.slow_mode || 0); setChatMuted(r.muted_users || []); } catch {}
  };
  useEffect(() => { if (!user) return; loadChat(); const iv = setInterval(loadChat, 15000); return () => clearInterval(iv); /* eslint-disable-next-line */ }, [id, user]);

  // Live reactions: spawn floating emojis
  const spawnFloat = (emoji) => {
    const key = ++rxKey.current;
    setFloatRx((f) => [...f, { key, emoji, left: 8 + Math.random() * 78 }]);
    setTimeout(() => setFloatRx((f) => f.filter((x) => x.key !== key)), 2600);
  };
  const loadReactions = async () => {
    try {
      const r = await api.get(`/shows/${id}/reactions`);
      setRxTotal(r.total || 0);
      if (r.emojis) setRxEmojis(r.emojis);
    } catch {}
  };
  const react = async (emoji) => {
    spawnFloat(emoji);
    setRxTotal((t) => t + 1);
    try { await api.post(`/shows/${id}/reaction`, { emoji }); } catch {}
  };
  // Initial totals only; WebSocket streams the live floats.
  useEffect(() => { if (!user) return; loadReactions(); /* eslint-disable-next-line */ }, [id, user]);

  // ── Real-time WebSocket: instant chat, bids, reactions, fundraising & viewers ──
  useEffect(() => {
    if (!user || !id) return;
    realtime.subscribeShow(id);
    const offChat = realtime.on("chat", (m) => {
      const message = m.message;
      if (!message || message.show_id !== id) return;
      setChat((prev) => (prev.some((x) => x.id === message.id) ? prev : [...prev, message]));
    });
    const offBid = realtime.on("bid", (m) => {
      setData((prev) => {
        if (!prev) return prev;
        return { ...prev, lots: prev.lots.map((l) => (l.id === m.lot_id ? { ...l, ...(m.lot || {}) } : l)) };
      });
    });
    const offReaction = realtime.on("reaction", (m) => {
      if (m.user_id && m.user_id === chatMe) return; // my own reaction already spawned locally
      spawnFloat(m.emoji);
      setRxTotal((t) => t + 1);
    });
    const offFund = realtime.on("fundraise", (m) => {
      if (m.show_id !== id) return;
      if (m.raised_amount !== undefined) setData((prev) => (prev ? { ...prev, show: { ...prev.show, raised_amount: m.raised_amount } } : prev));
      if (m.amount && m.donor_name) {
        setShout({ key: Date.now(), donor: m.donor_name, amount: m.amount, tier: m.tier_title });
        clearTimeout(shoutTimer.current);
        shoutTimer.current = setTimeout(() => setShout(null), 6000);
      }
    });
    const offPresence = realtime.on("presence", (m) => {
      if (m.show_id === id) { setLiveViewers(m.viewers); if (m.roster) setRoster(m.roster); }
    });
    const offSpot = realtime.on("spotlight", (m) => {
      if (m.show_id !== id) return;
      setData((prev) => (prev ? { ...prev, show: { ...prev.show, spotlight_lot_id: m.lot_id } } : prev));
    });
    const applyLot = (m, { setActive } = {}) => {
      if (!m.lot) return;
      setData((prev) => {
        if (!prev) return prev;
        const lots = prev.lots.map((l) => (l.id === m.lot_id ? { ...l, ...m.lot } : l));
        let show = prev.show;
        if (setActive === "on") show = { ...show, active_lot_id: m.lot_id };
        if (setActive === "off" && show.active_lot_id === m.lot_id) show = { ...show, active_lot_id: null };
        return { ...prev, show, lots };
      });
    };
    const offLotAct = realtime.on("lot_activated", (m) => { if (m.show_id ? m.show_id === id : true) applyLot(m, { setActive: "on" }); });
    const offLotClose = realtime.on("lot_closed", (m) => { if (m.show_id ? m.show_id === id : true) applyLot(m, { setActive: "off" }); });
    const offLotTimer = realtime.on("lot_timer", (m) => { if (m.show_id ? m.show_id === id : true) applyLot(m); });
    // initial roster snapshot
    api.get(`/shows/${id}/viewers`).then((r) => { setLiveViewers(r.viewers); setRoster(r.roster || []); }).catch(() => {});
    return () => { realtime.unsubscribeShow(id); offChat(); offBid(); offReaction(); offFund(); offPresence(); offSpot(); offLotAct(); offLotClose(); offLotTimer(); };
    /* eslint-disable-next-line */
  }, [id, user, chatMe]);

  // Auto-scroll ONLY the chat container (never the page), and only when new
  // messages arrive while the user is already near the bottom.
  useEffect(() => {
    const box = chatBox.current;
    if (!box) { prevChatLen.current = chat.length; return; }
    const added = chat.length > prevChatLen.current;
    const nearBottom = box.scrollHeight - box.scrollTop - box.clientHeight < 120;
    if (added && nearBottom) box.scrollTop = box.scrollHeight;
    prevChatLen.current = chat.length;
  }, [chat]);

  // Confetti when a giveaway winner appears
  useEffect(() => {
    const w = data?.show?.giveaway?.winner_id;
    if (w && prevWinner.current !== w) { prevWinner.current = w; setConfetti(true); }
  }, [data?.show?.giveaway?.winner_id]);

  const isHostLive = data?.show && user?.user_id === data.show.seller_id && data.show.status === "live";
  useEffect(() => {
    if (!isHostLive) return;
    let alive = true;
    const fetchSim = () => api.get(`/shows/${id}/simulcast`).then((d) => { if (alive) setSimulcast(d.simulcast); }).catch(() => {});
    fetchSim();
    const t = setInterval(fetchSim, 8000);
    return () => { alive = false; clearInterval(t); };
  }, [isHostLive, id]);

  if (loading) return <Loading />;
  if (!data) return <Empty title="Show not found" sub="It may have ended or been removed." />;
  const { show, lots } = data;
  const isHost = user?.user_id === show.seller_id;
  const giveaway = show.giveaway;
  const spotlightLot = (lots || []).find((l) => l.id === show.spotlight_lot_id) || null;
  const activeLot = (lots || []).find((l) => l.id === show.active_lot_id) || null;
  const featureLot = spotlightLot || activeLot;
  const shipLabel = (lot) => {
    const pkg = (lot && lot.package) || {};
    if (!pkg.requires_shipping) return "No shipping needed";
    if (pkg.free_shipping) return "Free shipping";
    if (pkg.flat_fee > 0) return `+ ${money(pkg.flat_fee)} shipping`;
    return "Shipping at checkout";
  };

  const like = async () => { setHearts((h) => h + 1); try { await api.post(`/shows/${id}/heart`); } catch {} };

  const goLive = async () => {
    try { await api.post(`/shows/${id}/go-live`); toast.success("You're live!"); await load(true); }
    catch (e) { toast.error(e.message || "Could not go live"); }
  };
  const endShow = async () => {
    if (!window.confirm("End this show now? Viewers will stop seeing your live feed.")) return;
    try { await api.post(`/shows/${id}/end`); toast.success("Show ended"); await load(true); }
    catch (e) { toast.error(e.message || "Could not end show"); }
  };
  const deleteShow = async () => {
    if (!window.confirm("Delete this show permanently? This removes the show and its items.")) return;
    try { await api.del(`/shows/${id}`); toast.success("Show deleted"); navigate("/my-shows"); }
    catch (e) { toast.error(e.message || "Could not delete show"); }
  };
  const toggleSimulcast = async () => {
    const on = simulcast?.active;
    setSimBusy(true);
    try {
      const d = await api.post(`/shows/${id}/simulcast/${on ? "stop" : "start"}`, on ? undefined : { template_base: window.location.origin });
      setSimulcast(d.simulcast);
      toast.success(on ? "Simulcast stopped" : "Simulcasting to your channels 🔴");
    } catch (e) {
      if (e.message && e.message.includes("destination")) toast.error("Add a destination in Simulcast settings first");
      else toast.error(e.message || "Could not toggle simulcast");
    } finally { setSimBusy(false); }
  };

  const buyNow = (lot) => setCheckoutLot(lot);
  const addToCart = (lot) => {
    cart.add({ lot_id: lot.id, title: lot.title, image: lot.image, price: lot.price || lot.current_bid || 0,
               show_id: id, show_title: show?.title, seller_name: show?.seller_name });
    toast.success("Added to cart");
  };

  const toggleSpotlight = async (lot) => {
    const next = show.spotlight_lot_id === lot.id ? null : lot.id;
    setData((prev) => (prev ? { ...prev, show: { ...prev.show, spotlight_lot_id: next } } : prev));
    try { await api.patch(`/shows/${id}/spotlight`, { lot_id: next }); toast.success(next ? "Featured on screen" : "Removed from screen"); }
    catch (e) { toast.error(e.message || "Could not update spotlight"); load(true); }
  };

  // ── Host auction run controls: activate / close / timer ──
  const activateLot = async (lot) => {
    try { await api.post(`/shows/${id}/lots/${lot.id}/activate`); toast.success(`"${lot.title}" is now the active item`); await load(true); }
    catch (e) { toast.error(e.message || "Could not activate item"); }
  };
  const closeLot = async (lot) => {
    const isAuction = lot.type === "auction";
    if (!window.confirm(`Close "${lot.title}"? ${isAuction ? "The highest bidder wins it." : "This ends the listing."}`)) return;
    try {
      const res = await api.post(`/shows/${id}/lots/${lot.id}/close`);
      toast.success(res.status === "sold" ? `Sold to ${res.winner_name || "the winner"}! 🎉` : "Item closed");
      await load(true);
    } catch (e) { toast.error(e.message || "Could not close item"); }
  };
  const extendTimer = async (lot, seconds) => {
    try { await api.post(`/shows/${id}/lots/${lot.id}/timer`, { extend: seconds }); toast.success(`+${seconds}s added to the timer`); await load(true); }
    catch (e) { toast.error(e.message || "Could not update timer"); }
  };

  // ── Theater / fullscreen for the live stage ──
  const toggleFullscreen = () => {
    const el = stageRef.current;
    if (!el) return;
    if (document.fullscreenElement) { document.exitFullscreen?.(); }
    else { (el.requestFullscreen?.() || Promise.reject()).catch(() => toast.error("Fullscreen isn't supported on this browser")); }
  };

  const placeBid = async (lot) => {
    const current = lot.current_bid || lot.starting_bid || lot.price || 0;
    const suggested = lot.high_bidder_id ? current + 5 : current;
    const input = window.prompt(`Enter your bid (min $${lot.high_bidder_id ? current + 1 : current})`, String(suggested));
    if (!input) return;
    setBusy(lot.id);
    try { await api.post(`/shows/${id}/lots/${lot.id}/bid`, { amount: Number(input) }); toast.success("Bid placed!"); await load(true); }
    catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const sendMsg = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    const text = msg.trim();
    setMsg("");
    try { await api.post(`/shows/${id}/chat`, { text }); await loadChat(); }
    catch (err) { setMsg(text); toast.error(err.message); }
  };

  const blockHost = async () => {
    if (!window.confirm(`Block ${show.seller_name}? You won't see their messages and they can't contact you.`)) return;
    try { await api.post(`/blocks/${show.seller_id}`); toast.success(`Blocked ${show.seller_name}`); await loadChat(); }
    catch (e) { toast.error(e.message); }
  };
  const blockChatter = async (userId, name) => {
    if (!userId) return;
    try { await api.post(`/blocks/${userId}`); toast.success(`Blocked ${name}`); await loadChat(); }
    catch (e) { toast.error(e.message); }
  };
  const setSlowMode = async (secs) => {
    try { await api.patch(`/shows/${id}/chat-settings`, { slow_mode: secs }); setChatSlow(secs); toast.success(secs ? `Slow mode: ${secs}s` : "Slow mode off"); }
    catch (e) { toast.error(e.message); }
  };
  const muteChatter = async (userId, name) => {
    try { await api.post(`/shows/${id}/chat/mute/${userId}`); toast.success(`Muted ${name}`); await loadChat(); }
    catch (e) { toast.error(e.message); }
  };
  const removeMessage = async (mid) => {
    try { await api.del(`/shows/${id}/chat/${mid}`); await loadChat(); }
    catch (e) { toast.error(e.message); }
  };

  const startGiveaway = async () => {
    const prize = window.prompt("What's the giveaway prize?", "Free item of the night 🎁");
    if (!prize) return;
    try { await api.post(`/shows/${id}/giveaway/start`, { prize }); toast.success("Giveaway started!"); load(true); }
    catch (e) { toast.error(e.message); }
  };
  const joinGiveaway = async () => {
    try { const r = await api.post(`/shows/${id}/giveaway/join`); toast.success("You're in! 🤞"); load(true); }
    catch (e) { toast.error(e.message); }
  };
  const drawWinner = async () => {
    try { const r = await api.post(`/shows/${id}/giveaway/draw`); setConfetti(true); toast.success(`Winner: ${r.winner_name}!`); load(true); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="fade-up show-detail-page">
      {reportTarget && <ReportModal target={reportTarget} onClose={() => setReportTarget(null)} />}
      {confetti && <Confetti onDone={() => setConfetti(false)} />}
      {checkoutLot && <CheckoutModal showId={id} lot={checkoutLot} onClose={() => setCheckoutLot(null)} />}
      {donate && <DonateModal showId={id} preset={donate} onClose={() => setDonate(null)} />}
      {cart.count > 0 && !checkoutLot && (
        <div className="cart-bar" data-testid="cart-bar" style={{ position: "fixed", bottom: 22, right: 22, zIndex: 40, background: "var(--brand)", color: "#fff", borderRadius: 14, padding: "12px 16px", display: "flex", alignItems: "center", gap: 14, boxShadow: "0 10px 30px rgba(0,0,0,.25)" }}>
          <ShoppingBag size={18} />
          <div style={{ fontWeight: 800 }}>{cart.count} in cart</div>
          <button className="ss-btn ss-btn-gold ss-btn-sm" onClick={() => navigate("/cart")} data-testid="cart-goto">View cart</button>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 340px", gap: 22, alignItems: "start" }} className="show-layout">
        <div>
          <div className="stage" data-testid="show-stage" ref={stageRef}>
            {show.status === "live" ? (
              <LiveVideoPlayer showId={id} isHost={isHost} poster={show.thumbnail} onGoLive={() => load(true)} />
            ) : show.stream_url ? (
              <video src={show.stream_url} poster={show.thumbnail} autoPlay muted loop playsInline style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : <img src={show.thumbnail} alt={show.title} />}
            <div className="stage-overlay" />
            <button onClick={toggleFullscreen} data-testid="theater-toggle" title={isFs ? "Exit fullscreen" : "Theater / fullscreen"}
              style={{ position: "absolute", top: 14, right: 14, background: "rgba(0,0,0,.5)", color: "#fff", border: "none", borderRadius: 999, width: 38, height: 38, display: "grid", placeItems: "center", cursor: "pointer", zIndex: 7 }}>
              {isFs ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </button>
            {/* SouqStream + Halal branding overlay */}
            <div style={{ position: "absolute", top: 14, right: 62, display: "flex", alignItems: "center", gap: 8, pointerEvents: "none", zIndex: 7 }} data-testid="stage-logos">
              <img src="/assets/souqstream-logo.png" alt="SouqStream" style={{ height: 34, width: "auto", filter: "drop-shadow(0 2px 6px rgba(0,0,0,.6))" }} data-testid="stage-logo-souqstream" />
              <img src="/assets/halal-logo.jpg" alt="Halal certified" style={{ height: 36, width: 36, borderRadius: "50%", objectFit: "cover", border: "1.5px solid var(--gold)", boxShadow: "0 2px 8px rgba(0,0,0,.55)" }} data-testid="stage-logo-halal" />
            </div>

            {isFs && (
              <div data-testid="theater-chat" style={{ position: "absolute", right: 16, bottom: 72, width: 360, maxWidth: "34%", maxHeight: "60%", display: "flex", flexDirection: "column", background: "rgba(0,0,0,.42)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)", borderRadius: 14, padding: 12, zIndex: 8, border: "1px solid rgba(255,255,255,.14)" }}>
                <div style={{ color: "#fff", fontWeight: 800, fontSize: 13, marginBottom: 8, opacity: 0.9 }}>Live chat</div>
                <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column-reverse", gap: 6 }}>
                  {chat.length === 0 && <div style={{ color: "rgba(255,255,255,.7)", fontSize: 13 }}>Be the first to say something 👋</div>}
                  {[...chat].slice(-40).reverse().map((m) => (
                    <div key={m.id} style={{ fontSize: 13, color: "#fff", lineHeight: 1.4 }}>
                      <span style={{ fontWeight: 800, color: m.user_id === chatMe ? "var(--gold)" : "#ffd98a" }}>{m.user_id === chatMe ? "You" : m.user_name}</span>{" "}
                      <span style={{ opacity: 0.96 }}>{m.text}</span>
                    </div>
                  ))}
                </div>
                {user && (
                  <form onSubmit={sendMsg} style={{ display: "flex", gap: 6, marginTop: 8 }}>
                    <input placeholder="Say something…" value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="theater-chat-input"
                      style={{ flex: 1, height: 38, borderRadius: 999, border: "1px solid rgba(255,255,255,.3)", background: "rgba(255,255,255,.12)", color: "#fff", padding: "0 14px", outline: "none", fontSize: 14 }} />
                    <button className="ss-btn ss-btn-gold" style={{ height: 38, padding: "0 12px" }} data-testid="theater-chat-send"><Send size={15} /></button>
                  </form>
                )}
              </div>
            )}
            <div style={{ position: "absolute", top: 14, left: 14, display: "flex", gap: 8 }}>
              {show.status === "live" ? <span className="tag tag-live"><span className="ss-dot" style={{ background: "#fff" }} /> Live</span> : <StatusPill status={show.status} />}
              <span className="tag" style={{ background: "rgba(0,0,0,.5)", color: "#fff" }} data-testid="live-viewers"><Eye size={12} /> {(liveViewers != null ? Math.max(liveViewers, show.viewer_count || 0) : (show.viewer_count || 0)).toLocaleString()}</span>
            </div>
            <button onClick={like} data-testid="heart-button" style={{ position: "absolute", bottom: 14, right: 14, background: "rgba(0,0,0,.5)", color: "#fff", border: "none", borderRadius: 999, padding: "8px 14px", display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontWeight: 700 }}>
              <Heart size={16} fill="var(--red)" color="var(--red)" /> {hearts.toLocaleString()}
            </button>

            {/* Floating reactions */}
            <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }} data-testid="reaction-layer">
              {floatRx.map((f) => (
                <span key={f.key} className="reaction-float" style={{ left: `${f.left}%` }}>{f.emoji}</span>
              ))}
            </div>

            {/* Donation thank-you shout-out */}
            {shout && (
              <div key={shout.key} className="donate-shout" data-testid="donate-shout"
                style={{ position: "absolute", top: 16, left: "50%", transform: "translateX(-50%)", display: "flex", alignItems: "center", gap: 10, background: "linear-gradient(90deg, var(--brand), var(--brand-2, #0C4A38))", color: "#fff", borderRadius: 999, padding: "10px 18px", boxShadow: "0 10px 30px rgba(0,0,0,.35)", border: "1.5px solid var(--gold)", maxWidth: "90%", zIndex: 6 }}>
                <HandHeart size={20} style={{ color: "var(--gold)", flexShrink: 0 }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 800, fontSize: 15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {shout.donor} donated {money(Number(shout.amount))} 💚
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.9 }}>{shout.tier ? `${shout.tier} · ` : ""}Thank you for your support!</div>
                </div>
              </div>
            )}

            {/* Quick reaction bar */}
            <div data-testid="reaction-bar" style={{ position: "absolute", bottom: 14, left: 14, display: "flex", alignItems: "center", gap: 6, background: "rgba(0,0,0,.42)", borderRadius: 999, padding: "6px 10px", backdropFilter: "blur(6px)" }}>
              {rxEmojis.map((e) => (
                <button key={e} onClick={() => react(e)} data-testid={`react-${e}`} className="reaction-btn" title="React"
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: 2 }}>{e}</button>
              ))}
              {rxTotal > 0 && <span style={{ color: "#fff", fontWeight: 800, fontSize: 12, marginLeft: 4 }}>{rxTotal.toLocaleString()}</span>}
            </div>

            {/* Live item — active/spotlighted product pinned on screen for buyers */}
            {featureLot && (
              <div data-testid="spotlight-card" style={{ position: "absolute", ...(isFs ? { left: 16, right: "auto", maxWidth: 480, bottom: 96, zIndex: 9 } : { left: 14, right: 14, bottom: 60 }), display: "flex", alignItems: "center", gap: 12, background: "rgba(0,0,0,.62)", backdropFilter: "blur(8px)", border: "1px solid var(--gold)", borderRadius: 14, padding: "10px 12px", boxShadow: "0 10px 30px rgba(0,0,0,.35)" }}>
                <img src={featureLot.image} alt="" style={{ width: 56, height: 56, borderRadius: 10, objectFit: "cover", flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0, color: "#fff" }}>
                  {spotlightLot ? (
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "var(--gold)", color: "var(--brand-2)", fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 999, textTransform: "uppercase", letterSpacing: .4 }}><Sparkles size={11} /> Featured</div>
                  ) : (
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "var(--red)", color: "#fff", fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 999, textTransform: "uppercase", letterSpacing: .4 }}><Radio size={11} /> Bidding now</div>
                  )}
                  <div style={{ fontWeight: 800, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginTop: 3 }} data-testid="spotlight-title">{featureLot.title}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 800, color: "var(--gold)", fontSize: 15 }}>{money(featureLot.type === "auction" ? (featureLot.current_bid || featureLot.starting_bid || featureLot.price) : featureLot.price)}</span>
                    <span data-testid="spotlight-shipping" style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, opacity: .9 }}><Truck size={12} /> {shipLabel(featureLot)}</span>
                  </div>
                </div>
                {featureLot.status === "sold" ? (
                  <span className="ss-btn ss-btn-sm ss-btn-ghost" style={{ opacity: .7 }} data-testid="spotlight-sold">Sold</span>
                ) : featureLot.type === "auction" ? (
                  <button className="ss-btn ss-btn-gold ss-btn-sm" onClick={() => placeBid(featureLot)} data-testid="spotlight-bid"><Gavel size={14} /> Bid</button>
                ) : (
                  <button className="ss-btn ss-btn-gold ss-btn-sm" onClick={() => buyNow(featureLot)} data-testid="spotlight-buy"><CreditCard size={14} /> Buy now</button>
                )}
                {isHost && spotlightLot && (
                  <button className="disc-search" style={{ width: 30, height: 30, background: "rgba(255,255,255,.16)", color: "#fff", flexShrink: 0 }} onClick={() => toggleSpotlight(spotlightLot)} title="Remove from screen" data-testid="spotlight-clear"><X size={15} /></button>
                )}
              </div>
            )}
          </div>

          <div style={{ marginTop: 18 }}>
            <h1 style={{ fontSize: 26 }}>{show.title}</h1>
            {isHost && (
              <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }} data-testid="host-controls">
                {show.status !== "live" && show.status !== "ended" && (() => {
                  const emptyMarketplace = !show.is_fundraiser && lots.length === 0;
                  return (
                    <button className="ss-btn ss-btn-live ss-btn-sm" onClick={goLive} disabled={emptyMarketplace} data-testid="host-go-live"
                      title={emptyMarketplace ? "Add at least one item before going live" : "Go live now"}
                      style={emptyMarketplace ? { opacity: .55, cursor: "not-allowed" } : undefined}>
                      <Radio size={14} /> Go Live
                    </button>
                  );
                })()}
                {show.status === "live" && (
                  <button className="ss-btn ss-btn-ghost ss-btn-sm" onClick={endShow} data-testid="host-end-show"><Square size={14} /> End show</button>
                )}
                {show.status === "live" && (
                  <button className={`ss-btn ss-btn-sm ${simulcast?.active ? "ss-btn-live" : "ss-btn-gold"}`} onClick={toggleSimulcast} disabled={simBusy} data-testid="host-simulcast-toggle" title="Restream to YouTube, Facebook & Twitch">
                    <Radio size={14} /> {simBusy ? "…" : (simulcast?.active ? `Simulcasting (${(simulcast.destinations || []).length})` : "Simulcast")}
                  </button>
                )}
                <button className="ss-btn ss-btn-ghost ss-btn-sm" style={{ color: "var(--red)", borderColor: "var(--red)" }} onClick={deleteShow} data-testid="host-delete-show"><Trash2 size={14} /> Delete</button>
                {show.status === "scheduled" && show.scheduled_at && (
                  <span className="ss-muted" style={{ fontSize: 13, alignSelf: "center", display: "inline-flex", alignItems: "center", gap: 6 }}><Calendar size={13} /> Airs {new Date(show.scheduled_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                )}
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12 }}>
              <img src={show.seller_avatar} alt={show.seller_name} onClick={() => navigate(`/store/${show.seller_id}`)} style={{ width: 44, height: 44, borderRadius: 999, objectFit: "cover", border: "2px solid var(--gold)", cursor: "pointer" }} data-testid="show-seller-avatar" />
              <div>
                <div style={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 6 }}>
                  <span onClick={() => navigate(`/store/${show.seller_id}`)} style={{ cursor: "pointer" }} data-testid="show-seller-link">{show.seller_name}</span>
                  {show.seller_verified && <CheckCircle2 size={16} className="badge-verified" />}
                  {show.seller_founding && <span className="badge-founding"><Ribbon size={11} /> Founding{show.seller_founding_number ? ` #${show.seller_founding_number}` : ""}</span>}
                  {show.seller_review_count > 0 && (
                    <span data-testid="show-seller-rating" style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 13, fontWeight: 700 }}>
                      <Star size={14} style={{ color: "var(--gold)" }} fill="var(--gold)" /> {show.seller_rating?.toFixed(1)}
                      <span className="ss-muted" style={{ fontWeight: 500 }}>({show.seller_review_count})</span>
                    </span>
                  )}
                </div>
                <div className="ss-muted" style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                  {show.seller_username && <span style={{ fontWeight: 700 }} data-testid="show-seller-handle">@{show.seller_username}</span>}
                  {show.seller_username && <span>·</span>}
                  {show.category}
                </div>
              </div>
              {!isHost && (
                <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                  <button className="ss-btn ss-btn-ghost ss-btn-sm" onClick={() => setReportTarget({ id: show.seller_id, name: show.seller_name, context: "show", show_id: id })} data-testid="report-host" title="Report host"><Flag size={14} /> Report</button>
                  <button className="ss-btn ss-btn-ghost ss-btn-sm" style={{ color: "var(--red)" }} onClick={blockHost} data-testid="block-host" title="Block host"><Ban size={14} /></button>
                </div>
              )}
            </div>
          </div>

          {/* Watching now — live viewer roster */}
          {show.status === "live" && roster.length > 0 && (
            <div className="watch-roster" data-testid="viewer-roster">
              <div className="watch-avatars">
                {roster.slice(0, 8).map((v) => (
                  <img key={v.user_id} src={v.avatar} alt={v.name || ""} title={v.name || ""} className="watch-av" data-testid={`viewer-${v.user_id}`} />
                ))}
              </div>
              <span className="ss-muted" style={{ fontSize: 13 }}>
                <b style={{ color: "var(--text)" }}>{(liveViewers ?? roster.length).toLocaleString()}</b> watching now
              </span>
            </div>
          )}

          {/* Giveaway panel */}
          {(giveaway || isHost) && show.status === "live" && !show.is_fundraiser && (
            <GiveawayPanel giveaway={giveaway} isHost={isHost} onStart={startGiveaway} onJoin={joinGiveaway} onDraw={drawWinner} me={user?.user_id} />
          )}

          {show.is_fundraiser ? (
            <FundraiserPanel show={show} perks={lots} isHost={isHost} onReload={() => load(true)} onDonate={(amount, tier, perk) => setDonate({ amount, tier, perk })} />
          ) : (
            <>
              <h2 style={{ fontSize: 20, marginTop: 26, marginBottom: 12 }}>Items in this show</h2>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {lots.length === 0 && (
                  <div className="ss-card" style={{ padding: 20, textAlign: "center" }} data-testid="no-items">
                    <ShoppingBag size={26} style={{ color: "var(--muted)", marginBottom: 8 }} />
                    <div style={{ fontWeight: 700 }}>No items in this show yet</div>
                    <div className="ss-muted" style={{ fontSize: 13, marginTop: 4 }}>
                      {isHost ? "Add items from your inventory so buyers can shop this show." : "The seller hasn't added items to this show yet — check back soon."}
                    </div>
                  </div>
                )}
                {lots.map((lot) => (
                  <LotRow key={lot.id} lot={lot} showId={id} busy={busy} onBuy={buyNow} onBid={placeBid} onAddToCart={addToCart} inCart={cart.has(lot.id)} onSettled={() => load(true)} settledRef={settledRef} isLive={show.status === "live"} isHost={isHost} featured={show.spotlight_lot_id === lot.id} onSpotlight={toggleSpotlight} isActive={show.active_lot_id === lot.id} onActivate={activateLot} onClose={closeLot} onTimer={extendTimer} />
                ))}
              </div>
              {/* Support the seller — optional donation, shown under the items so products lead */}
              {!isHost && (
                <button className="ss-btn ss-btn-ghost ss-btn-block" style={{ marginTop: 16 }} onClick={() => setDonate({ amount: null, tier: null, perk: null })} data-testid="show-donate-btn">
                  <HandHeart size={16} /> Support the seller with a donation
                </button>
              )}
            </>
          )}
        </div>

        <div className="chat-box" style={{ height: 620 }} data-testid="chat-box">
          <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--line)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <span style={{ fontWeight: 800 }}>Live chat</span>
            {isHost ? (
              <select value={chatSlow} onChange={(e) => setSlowMode(Number(e.target.value))} data-testid="slowmode-select" className="ss-field" style={{ height: 32, width: 128, fontSize: 13, padding: "0 8px" }}>
                <option value={0}>Slow mode: Off</option>
                <option value={3}>Slow mode: 3s</option>
                <option value={5}>Slow mode: 5s</option>
                <option value={10}>Slow mode: 10s</option>
                <option value={30}>Slow mode: 30s</option>
              </select>
            ) : chatSlow > 0 ? (
              <span className="ss-pill pill--open" data-testid="slowmode-badge" style={{ fontSize: 11 }}>Slow mode · {chatSlow}s</span>
            ) : null}
          </div>
          <div className="chat-msgs" ref={chatBox}>
            {chat.length === 0 && <div className="ss-muted" style={{ fontSize: 13 }}>Be the first to say something 👋</div>}
            {chat.map((m) => {
              const mine = m.user_id === chatMe;
              return (
              <div key={m.id} style={{ fontSize: 14, display: "flex", alignItems: "flex-start", gap: 6 }} className="chat-row">
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 800, color: mine ? "var(--brand)" : "var(--gold-2)" }}>{mine ? "You" : m.user_name}</span>
                  {!mine && m.user_username && <span className="ss-muted" style={{ fontSize: 11, marginLeft: 3 }} data-testid={`chat-handle-${m.id}`}>@{m.user_username}</span>}
                  {" "}<span>{m.text}</span>
                </div>
                {(!mine || isHost) && (
                  <span className="chat-row-actions" style={{ display: "flex", gap: 2, opacity: 0.55 }}>
                    {!mine && <button title="Report" onClick={() => setReportTarget({ id: m.user_id, name: m.user_name, context: "live_chat", show_id: id })} data-testid={`chat-report-${m.id}`} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)", padding: 2 }}><Flag size={13} /></button>}
                    {!mine && !isHost && <button title="Block" onClick={() => blockChatter(m.user_id, m.user_name)} data-testid={`chat-block-${m.id}`} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)", padding: 2 }}><Ban size={13} /></button>}
                    {isHost && !mine && <button title="Mute in this show" onClick={() => muteChatter(m.user_id, m.user_name)} data-testid={`chat-mute-${m.id}`} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)", padding: 2 }}><VolumeX size={13} /></button>}
                    {isHost && <button title="Remove message" onClick={() => removeMessage(m.id)} data-testid={`chat-remove-${m.id}`} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--red)", padding: 2 }}><Trash2 size={13} /></button>}
                  </span>
                )}
              </div>
            );})}
            <div ref={chatEnd} />
          </div>
          <form onSubmit={sendMsg} style={{ padding: 12, borderTop: "1px solid var(--line)", display: "flex", gap: 8 }}>
            <div className="ss-field" style={{ flex: 1, height: 44 }}><input placeholder="Say something…" value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="chat-input" /></div>
            <button className="ss-btn ss-btn-primary" style={{ height: 44, padding: "0 14px" }} data-testid="chat-send"><Send size={16} /></button>
          </form>
        </div>
      </div>
    </div>
  );
}

function CheckoutModal({ showId, lot, onClose }) {
  const price = lot.price || lot.current_bid || 0;
  const [step, setStep] = useState("address");
  const [addr, setAddr] = useState({ name: "", phone: "", street1: "", street2: "", city: "", state: "", zip: "", country: "US" });
  const [rates, setRates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [requiresShipping, setRequiresShipping] = useState(true);
  const [warnings, setWarnings] = useState([]);
  const [busy, setBusy] = useState(false);

  const pay = async (rate) => {
    setBusy(true);
    try {
      const res = await api.post("/payments/checkout", {
        show_id: showId, lot_id: lot.id, origin_url: window.location.origin,
        ship_to: requiresShipping ? addr : null, shipping_rate: rate || null,
      });
      window.location.href = res.checkout_url;
    } catch (err) { toast.error(err.message || "Couldn't start checkout"); setBusy(false); }
  };

  const getRates = async (e) => {
    e.preventDefault();
    setBusy(true); setWarnings([]);
    try {
      const v = await api.post("/shipping/validate-address", { address: addr });
      if (!v.valid) { setWarnings(v.messages); setBusy(false); return; }
      const d = await api.post("/shipping/quote", { show_id: showId, lot_id: lot.id, ship_to: addr });
      if (d.requires_shipping === false) { setRequiresShipping(false); await pay(null); return; }
      setRates(d.rates); setSelected(d.rates[0]?.rate_id); setStep("rate"); setBusy(false);
    } catch (err) { toast.error(err.message); setBusy(false); }
  };

  const rate = rates.find((r) => r.rate_id === selected);
  const total = price + (rate?.buyer_charge || 0);

  return (
    <div className="ss-modal-back" onClick={onClose}>
      <div className="ss-modal" onClick={(e) => e.stopPropagation()} data-testid="checkout-modal">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h2 style={{ fontSize: 20 }}>Checkout</h2>
          <button className="disc-search" style={{ width: 34, height: 34 }} onClick={onClose} data-testid="checkout-close"><X size={18} /></button>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 14 }}>
          <img src={lot.image} alt="" style={{ width: 56, height: 56, borderRadius: 10, objectFit: "cover" }} />
          <div style={{ flex: 1 }}><div style={{ fontWeight: 800 }}>{lot.title}</div><div className="ss-muted" style={{ fontSize: 13 }}>Item {money(price)}</div></div>
        </div>

        {step === "address" && (
          <form onSubmit={getRates} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <AddressPicker onChange={setAddr} warnings={warnings} testPrefix="co" initial={addr} />
            <button className="ss-btn ss-btn-primary ss-btn-block" disabled={busy} data-testid="co-get-rates"><Truck size={16} /> {busy ? "Getting rates…" : "Get shipping rates"}</button>
          </form>
        )}

        {step === "rate" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontWeight: 800, fontSize: 14 }}>Choose shipping</div>
            {rates.map((r) => (
              <label key={r.rate_id} className="ss-card" style={{ padding: 12, display: "flex", alignItems: "center", gap: 10, cursor: "pointer", borderColor: selected === r.rate_id ? "var(--brand)" : "var(--line)" }} data-testid={r.flat ? "co-rate-flat" : `co-rate-${r.carrier}`.replace(/\s+/g, "-")}>
                <input type="radio" name="corate" checked={selected === r.rate_id} onChange={() => setSelected(r.rate_id)} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                    {r.flat ? (r.international ? "International flat rate" : "Flat rate shipping") : `${r.carrier} · ${r.service}`}
                    {r.flat && <span className="ss-pill" style={{ background: "var(--gold)", color: "var(--brand-2)", fontSize: 11 }} data-testid="co-flat-badge">{r.international ? "Intl" : "Set price"}</span>}
                  </div>
                  <div className="ss-muted" style={{ fontSize: 12 }}>{r.flat ? (r.international ? "Flat international rate set by the seller" : "One flat rate set by the seller") : r.delivery_window}</div>
                </div>
                <div style={{ fontWeight: 800 }}>{money(r.buyer_charge)}</div>
              </label>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 800, fontSize: 16, marginTop: 4 }}>
              <span>Total</span><span data-testid="co-total">{money(total)}</span>
            </div>
            <button className="ss-btn ss-btn-primary ss-btn-block" disabled={busy || !rate} onClick={() => pay(rate)} data-testid="co-pay"><CreditCard size={16} /> {busy ? "Redirecting…" : `Pay ${money(total)}`}</button>
            <button type="button" className="ss-btn ss-btn-ghost ss-btn-sm" onClick={() => setStep("address")} data-testid="co-back">Edit address</button>
          </div>
        )}
      </div>
    </div>
  );
}

function FundraiserPanel({ show, perks, isHost, onDonate, onReload }) {
  const goal = Number(show.goal_amount) || 0;
  const raised = Number(show.raised_amount) || 0;
  const pct = goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : 0;
  const reached = goal > 0 && raised >= goal;
  const [wall, setWall] = useState({ recent: [], top_donors: [], count: 0 });
  const [ending, setEnding] = useState(false);
  const firedRef = useRef(false);
  const [celebrate, setCelebrate] = useState(false);

  useEffect(() => {
    let alive = true;
    const fetchWall = () => api.get(`/shows/${show.id}/donations`).then((d) => { if (alive) setWall(d); }).catch(() => {});
    fetchWall();
    const t = setInterval(fetchWall, 8000);
    return () => { alive = false; clearInterval(t); };
  }, [show.id]);

  useEffect(() => {
    if (reached && !firedRef.current) { firedRef.current = true; setCelebrate(true); }
  }, [reached]);

  const endCampaign = async () => {
    if (!window.confirm("End this fundraiser and email yourself a recap?")) return;
    setEnding(true);
    try {
      const r = await api.post(`/shows/${show.id}/end`, {});
      toast.success(`Campaign ended · ${money(r.total)} raised from ${r.supporters} supporter(s). Recap emailed.`);
      onReload?.();
    } catch (e) { toast.error(e.message); } finally { setEnding(false); }
  };

  return (
    <div style={{ marginTop: 22 }} data-testid="fundraiser-panel">
      {celebrate && <Confetti onDone={() => setCelebrate(false)} />}
      {reached && (
        <div style={{ background: "var(--gold)", color: "#3a2a00", borderRadius: 14, padding: "14px 18px", fontWeight: 800, display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }} data-testid="goal-reached-banner">
          <Trophy size={20} /> Goal reached! 🎉 Thank you to every supporter.
        </div>
      )}

      <div className="ss-card" style={{ padding: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <HandHeart size={20} style={{ color: "var(--brand)" }} />
            <div style={{ fontWeight: 800, fontSize: 18 }}>Fundraiser</div>
          </div>
          {isHost && show.status !== "ended" && (
            <button className="ss-btn ss-btn-ghost ss-btn-sm" onClick={endCampaign} disabled={ending} data-testid="end-campaign-btn">
              {ending ? "Ending…" : "End & email recap"}
            </button>
          )}
          {show.status === "ended" && <span className="ss-pill pill--ended">Ended</span>}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 8 }}>
          <div style={{ fontSize: 26, fontWeight: 800 }} data-testid="raised-amount">{money(raised)}</div>
          {goal > 0 && <div className="ss-muted" style={{ fontSize: 14 }}>raised of {money(goal)}</div>}
        </div>
        {goal > 0 && (
          <div data-testid="progress-bar" style={{ height: 12, borderRadius: 999, background: "var(--line)", overflow: "hidden" }}>
            <div data-testid="progress-fill" style={{ width: `${pct}%`, height: "100%", background: reached ? "var(--gold-2, #b8860b)" : "var(--brand)", transition: "width .4s ease" }} />
          </div>
        )}
        {goal > 0 && <div className="ss-muted" style={{ fontSize: 13, marginTop: 6 }}>{pct}% funded · {wall.count} donation{wall.count === 1 ? "" : "s"}</div>}
        {show.status !== "ended" && (
          <button className="ss-btn ss-btn-primary ss-btn-block" style={{ marginTop: 16 }} onClick={() => onDonate(null, null, null)} data-testid="donate-btn">
            <HandHeart size={16} /> Donate
          </button>
        )}
      </div>

      {/* Donor wall */}
      {wall.recent.length > 0 && (
        <div className="ss-card" style={{ padding: 16, marginTop: 14 }} data-testid="donor-wall">
          <div style={{ fontWeight: 800, marginBottom: 10 }}>Recent supporters</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 220, overflow: "auto" }}>
            {wall.recent.map((d, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }} data-testid="donor-row">
                <div style={{ width: 30, height: 30, borderRadius: 999, background: "var(--gold-soft)", display: "grid", placeItems: "center", flexShrink: 0 }}><Heart size={14} style={{ color: "var(--brand)" }} /></div>
                <div style={{ flex: 1, fontSize: 14 }}>
                  <b>{d.donor_name}</b>{d.tier_title ? <span className="ss-muted"> · {d.tier_title}</span> : ""}
                </div>
                <div style={{ fontWeight: 800 }}>{money(d.amount)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {perks.length > 0 && show.status !== "ended" && (
        <>
          <h2 style={{ fontSize: 20, margin: "24px 0 12px" }}>Support with a perk</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
            {perks.map((p) => (
              <div className="ss-card" style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8 }} key={p.id} data-testid={`perk-${p.id}`}>
                <img src={p.image} alt="" style={{ width: "100%", height: 110, borderRadius: 10, objectFit: "cover" }} />
                <div style={{ fontWeight: 800 }}>{p.title}</div>
                {p.package?.requires_shipping && <span className="ss-muted" style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}><ShoppingBag size={12} /> Ships to you</span>}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "auto" }}>
                  <span style={{ fontWeight: 800 }}>{money(p.price)}</span>
                  <button className="ss-btn ss-btn-gold ss-btn-sm" onClick={() => onDonate(p.price, p.title, p)} data-testid={`perk-donate-${p.id}`}><Heart size={13} /> Donate</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function DonateModal({ showId, preset, onClose }) {
  const perk = preset?.perk || null;
  const needsShip = !!(perk && perk.package?.requires_shipping);
  const [amount, setAmount] = useState(preset?.amount ? String(preset.amount) : "");
  const [addr, setAddr] = useState({ name: "", phone: "", street1: "", street2: "", city: "", state: "", zip: "", country: "US" });
  const [warnings, setWarnings] = useState([]);
  const [busy, setBusy] = useState(false);
  const tier = preset?.tier || null;
  const quick = [10, 25, 50, 100];

  const give = async () => {
    const amt = Number(amount);
    if (!amt || amt < 1) return toast.error("Enter an amount of at least $1");
    setBusy(true); setWarnings([]);
    try {
      if (needsShip) {
        const v = await api.post("/shipping/validate-address", { address: addr });
        if (!v.valid) { setWarnings(v.messages); setBusy(false); return; }
      }
      const res = await api.post("/payments/donate", {
        show_id: showId, amount: amt, tier_title: tier,
        lot_id: perk?.id, ship_to: needsShip ? addr : null, origin_url: window.location.origin,
      });
      window.location.href = res.checkout_url;
    } catch (err) { toast.error(err.message || "Couldn't start donation"); setBusy(false); }
  };

  return (
    <div className="ss-modal-back" onClick={onClose}>
      <div className="ss-modal" onClick={(e) => e.stopPropagation()} data-testid="donate-modal">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h2 style={{ fontSize: 20 }}>{tier ? `Donate · ${tier}` : "Make a donation"}</h2>
          <button className="disc-search" style={{ width: 34, height: 34 }} onClick={onClose} data-testid="donate-close"><X size={18} /></button>
        </div>
        {!tier && (
          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            {quick.map((q) => (
              <button key={q} type="button" className={`gl-chip ${Number(amount) === q ? "active" : ""}`} onClick={() => setAmount(String(q))} data-testid={`donate-quick-${q}`}>${q}</button>
            ))}
          </div>
        )}
        <label className="ss-label">Amount ($)</label>
        <div className="ss-field" style={{ height: 46 }}>
          <span className="ss-muted">$</span>
          <input type="number" min="1" step="1" placeholder="Enter amount" value={amount} onChange={(e) => setAmount(e.target.value)} disabled={!!tier} data-testid="donate-amount" />
        </div>

        {needsShip && (
          <div style={{ marginTop: 14 }}>
            <AddressPicker onChange={setAddr} warnings={warnings} testPrefix="donate" initial={addr} />
          </div>
        )}

        <button className="ss-btn ss-btn-primary ss-btn-block" style={{ marginTop: 16 }} disabled={busy} onClick={give} data-testid="donate-confirm">
          <HandHeart size={16} /> {busy ? "Redirecting…" : `Donate ${amount ? money(Number(amount)) : ""}`}
        </button>
      </div>
    </div>
  );
}

function GiveawayPanel({ giveaway, isHost, onStart, onJoin, onDraw, me }) {
  if (!giveaway) {
    return (
      <div className="ss-card" style={{ padding: 18, marginTop: 20, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }} data-testid="giveaway-panel">
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}><div className="stat-ic"><Gift size={20} /></div><div><div style={{ fontWeight: 800 }}>Run a live giveaway</div><div className="ss-muted" style={{ fontSize: 13 }}>Pick a random winner from your viewers.</div></div></div>
        <button className="ss-btn ss-btn-gold ss-btn-sm" onClick={onStart} data-testid="giveaway-start"><Gift size={15} /> Start giveaway</button>
      </div>
    );
  }
  if (giveaway.status === "drawn") {
    return (
      <div className="spotlight" style={{ marginTop: 20 }} data-testid="giveaway-winner">
        <Trophy size={30} />
        <h3 style={{ marginTop: 6 }}>🎉 {giveaway.winner_name} won!</h3>
        <div style={{ fontWeight: 700, marginTop: 4 }}>Prize: {giveaway.prize}</div>
      </div>
    );
  }
  const joined = (giveaway.entries || []).includes(me);
  return (
    <div className="ss-card" style={{ padding: 18, marginTop: 20, borderColor: "var(--gold)" }} data-testid="giveaway-panel">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <div className="stat-ic" style={{ background: "var(--gold)", color: "var(--brand-2)" }}><Gift size={20} /></div>
          <div><div style={{ fontWeight: 800 }}>Giveaway: {giveaway.prize}</div><div className="ss-muted" style={{ fontSize: 13 }}>{(giveaway.entries || []).length} entered</div></div>
        </div>
        {isHost ? (
          <button className="ss-btn ss-btn-primary ss-btn-sm" onClick={onDraw} data-testid="giveaway-draw"><Trophy size={15} /> Draw winner</button>
        ) : (
          <button className="ss-btn ss-btn-gold ss-btn-sm" onClick={onJoin} disabled={joined} data-testid="giveaway-join">{joined ? "You're in 🤞" : "Join giveaway"}</button>
        )}
      </div>
    </div>
  );
}

function LotRow({ lot, busy, onBuy, onBid, onAddToCart, inCart, onSettled, showId, settledRef, isLive, isHost, featured, onSpotlight, isActive, onActivate, onClose, onTimer }) {
  const isAuction = lot.type === "auction";
  const left = useCountdown(isAuction ? lot.bid_end_at : null);
  const sold = lot.status === "sold";
  const price = isAuction ? (lot.current_bid || lot.starting_bid || lot.price) : lot.price;
  const ended = isAuction && lot.bid_end_at && left === 0;
  const reserve = Number(lot.reserve_price || 0);
  const hasReserve = isAuction && reserve > 0;
  const reserveNotMetFinal = lot.status === "reserve_not_met";
  const reserveMet = hasReserve && (sold || Number(price || 0) >= reserve);

  // Auto-settle when an auction timer hits zero
  useEffect(() => {
    if (isAuction && lot.bid_end_at && left === 0 && !sold && !settledRef.current[lot.id]) {
      settledRef.current[lot.id] = true;
      api.post(`/shows/${showId}/lots/${lot.id}/settle`).then(() => onSettled()).catch(() => {});
    }
  }, [left, isAuction, sold, lot.bid_end_at, lot.id, showId]);

  return (
    <div className="lot-row" data-testid={`lot-${lot.id}`} style={featured ? { borderColor: "var(--gold)", boxShadow: "0 0 0 1px var(--gold)" } : undefined}>
      <img src={lot.image} alt={lot.title} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          {lot.title}
          {isActive && !sold && <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "var(--red)", color: "#fff", fontSize: 10, fontWeight: 800, padding: "2px 7px", borderRadius: 999 }} data-testid={`active-badge-${lot.id}`}><Radio size={10} /> Live now</span>}
          {featured && <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "var(--gold-soft)", color: "var(--brand)", fontSize: 10, fontWeight: 800, padding: "2px 7px", borderRadius: 999 }} data-testid={`featured-badge-${lot.id}`}><Sparkles size={10} /> On screen</span>}
        </div>
        <div className="ss-muted" style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
          {isAuction ? <><Gavel size={13} /> {lot.high_bidder_name ? `High: ${lot.high_bidder_name}` : "Auction"}</> : <><ShoppingBag size={13} /> Buy now</>}
        </div>
        {(hasReserve || reserveNotMetFinal) && (
          reserveNotMetFinal ? (
            <span data-testid={`reserve-badge-${lot.id}`} style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 5, background: "var(--red)", color: "#fff", fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 999 }}>Reserve not met</span>
          ) : reserveMet ? (
            <span data-testid={`reserve-badge-${lot.id}`} style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 5, background: "var(--brand)", color: "#fff", fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 999 }}><CheckCircle2 size={11} /> Reserve met</span>
          ) : (
            <span data-testid={`reserve-badge-${lot.id}`} style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 5, background: "var(--gold-soft)", color: "var(--brand)", fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 999 }}>Reserve not met · {money(reserve)}</span>
          )
        )}
        {isAuction && lot.bid_end_at && !sold && (
          <div className="bid-timer" data-testid={`timer-${lot.id}`} style={{ marginTop: 4 }}>{ended ? "Ended" : `⏱ ${left}s`}</div>
        )}
        {isHost && isLive && !sold && (
          <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }} data-testid={`host-lot-controls-${lot.id}`}>
            {!isActive ? (
              <button className="ss-btn ss-btn-sm ss-btn-primary" onClick={() => onActivate(lot)} data-testid={`activate-${lot.id}`}>
                <Play size={13} /> Activate
              </button>
            ) : (
              <button className="ss-btn ss-btn-sm ss-btn-ghost" style={{ color: "var(--red)", borderColor: "var(--red)" }} onClick={() => onClose(lot)} data-testid={`close-lot-${lot.id}`}>
                <Square size={13} /> Close item
              </button>
            )}
            {isAuction && isActive && (
              <>
                <button className="ss-btn ss-btn-sm ss-btn-ghost" onClick={() => onTimer(lot, 15)} data-testid={`timer-add-15-${lot.id}`}><Clock size={13} /> +15s</button>
                <button className="ss-btn ss-btn-sm ss-btn-ghost" onClick={() => onTimer(lot, 30)} data-testid={`timer-add-30-${lot.id}`}><Clock size={13} /> +30s</button>
              </>
            )}
            <button className="ss-btn ss-btn-sm ss-btn-ghost" style={{ borderColor: featured ? "var(--gold)" : "var(--line)", color: featured ? "var(--brand)" : undefined }} onClick={() => onSpotlight(lot)} data-testid={`feature-${lot.id}`}>
              <Sparkles size={13} /> {featured ? "Unpin" : "Feature"}
            </button>
          </div>
        )}
      </div>
      <div style={{ textAlign: "right" }}>
        <div style={{ fontWeight: 800, fontSize: 18 }}>{money(price)}</div>
        {sold ? (
          <button className="ss-btn ss-btn-sm ss-btn-ghost" style={{ marginTop: 6 }} disabled data-testid={`sold-${lot.id}`}>Sold</button>
        ) : isAuction ? (
          <button className="ss-btn ss-btn-sm ss-btn-primary" style={{ marginTop: 6 }} disabled={ended || busy === lot.id} onClick={() => onBid(lot)} data-testid={`bid-${lot.id}`}>{ended ? "Ended" : "Place bid"}</button>
        ) : (
          <>
            <button className="ss-btn ss-btn-sm ss-btn-primary" style={{ marginTop: 6 }} disabled={busy === lot.id} onClick={() => onBuy(lot)} data-testid={`buy-${lot.id}`}><CreditCard size={13} /> {busy === lot.id ? "…" : "Buy now"}</button>
            <button className="ss-btn ss-btn-sm ss-btn-ghost" style={{ marginTop: 6, marginLeft: 6 }} disabled={inCart} onClick={() => onAddToCart(lot)} data-testid={`add-cart-${lot.id}`}>{inCart ? "In cart" : "+ Cart"}</button>
          </>
        )}
      </div>
    </div>
  );
}
